import { computed, reactive, Ref } from "vue";
import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import api from "@/services/api";
import { PaginatedResponse } from "@/services/utils";
import { useFeatureGate } from "@/services/feature_gate";
import { useActiveOrganizationId } from "@/services/user";

/**
 * AI Agent Context - captured from current view state
 * Sent with the first message to provide context to the AI agent
 */
export interface AIAgentContext {
  // View information
  viewName?: string; // e.g., "marketing_performance"
  routePath?: string; // e.g., "/marketing/performance"

  // Dashboard context - when viewing/editing a dashboard
  dashboardId?: number;
  dashboardTab?: { index: number; name: string };

  // Table aggregation level (from tableLevel query param)
  // Indicates which level of data the user is viewing: campaign, ad_group, ad, keyword, etc.
  tableLevel?: string;

  // Global filters from data_filters store
  temporalFilter?: {
    selection: { preset: string; start: string; end: string };
    comparison?: { preset: string; start: string; end: string };
  };
  includedResources?: number[];
  includedChannels?: string[];
  includedTags?: number[];
  excludedTags?: number[];
  includedConversionGroups?: number[];
  includedAdNetworks?: string[];

  // Entity drill-down from route.query
  entityContext?: {
    level: string; // 'campaign', 'adGroup', 'ad', etc.
    id: number;
    name?: string; // Optional - may not be in URL
    channel?: string; // Optional - may not be in URL
  };

  // Page-specific filters
  metricFilters?: {
    metric: string;
    operator: string;
    value: number;
  }[];
}

/**
 * AI Agent Session - conversation session
 */
export interface AIAgentSession {
  id: number;
  title: string;
  provider: string;
  model: string;
  status: "active" | "completed" | "error" | "archived";
  totalIterations: number;
  creditsUsed: string; // DRF-serialized Decimal, e.g. "12.7500"
  // Which effort levels this session's model accepts, served by the backend.
  // Empty means the model takes no effort setting, so there is no control to
  // show. Optional because a cached session payload predating this field
  // deserializes without it.
  supportedReasoningEfforts?: ReasoningEffort[];
  // Whether the run held its lease at the last read. A single false can be a
  // cache flush: see LEASE_MISSES_BEFORE_GONE. Null when the cache can't say.
  // The detail and run-state payloads carry it.
  runAlive?: boolean | null;
  // A stopped run's own lease: a tool running at the Stop can still save its
  // change. Same caveats as `runAlive`.
  runFinishing?: boolean | null;
  // The turn a Stop names. Detail and run-state payloads only.
  turn?: string | null;
  createdAt: string;
  modifiedAt: string;
}

export type AIAgentRunState = Pick<
  AIAgentSession,
  "id" | "status" | "runAlive" | "runFinishing" | "turn" | "modifiedAt"
>;

/**
 * Reasoning effort levels supported by GPT-5-family models. Kept in sync
 * with `ALLOWED_REASONING_EFFORTS` in backend/ai_agents/serializers/session.py.
 */
export type ReasoningEffort = "minimal" | "low" | "medium" | "high";

export const REASONING_EFFORT_OPTIONS: {
  value: ReasoningEffort;
  label: string;
}[] = [
  { value: "minimal", label: "Minimal" },
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
];

export const DEFAULT_REASONING_EFFORT: ReasoningEffort = "low";

/**
 * Organization AI defaults. Read by the pre-session prompt panel for the one
 * thing it cannot get from a session: which effort levels the model a new
 * session will run on accepts.
 */
export interface AIAgentConfig {
  defaultProvider: string;
  defaultModel: string;
  // Same contract as the session payload's field: empty means the model takes
  // no effort setting, so there is no control to show. Optional so a frontend
  // deployed ahead of the backend hides the control rather than offering
  // levels it cannot vouch for.
  supportedReasoningEfforts?: ReasoningEffort[];
}

/**
 * File attachment metadata (images uploaded to CDN)
 */
export interface ChatAttachment {
  url: string;
  key: string;
  contentType: string;
  filename: string;
}

/**
 * AI Agent Message - individual message in a session
 */
export interface AIAgentMessage {
  id: number;
  role: "system" | "user" | "assistant" | "tool";
  content: string;
  attachments?: ChatAttachment[];
  toolCalls?: {
    id: string;
    name: string;
    arguments: Record<string, unknown>;
  }[];
  toolCallId?: string | null;
  tokensUsed: number;
  createdAt: string;
}

/**
 * Tool Execution - result of a tool call
 */
export interface ToolExecution {
  toolName: string;
  arguments: Record<string, unknown>;
  result: string;
  success: boolean;
  durationMs: number;
}

/**
 * Chat Response - response from sending a message
 */
export interface ChatResponse {
  success: boolean;
  response: string;
  toolExecutions: ToolExecution[];
  iterations: number;
  tokensUsed: number;
  error?: string;
}

// List all sessions for the current user. The endpoint is paginated
// ({ count, next, previous, results }); unwrap `results` here so the query
// cache stays a plain array for all consumers (slice, setQueryData updates).
function listSessions(): Promise<AIAgentSession[]> {
  return api
    .get<PaginatedResponse<AIAgentSession>>("/ai-agents/sessions/")
    .then((response) => response.data.results);
}

export function useAISessionsQuery() {
  // Gated on the feature being usable: the AI sidepanel tab is now always
  // mounted (it renders the paywall for a non-entitled org), so its header
  // controls and prompt panel mount too. Without this the list request fires
  // for every locked org and 403s on repeat, filling Sentry's non-authed-403
  // bucket with noise no user can act on.
  const aiChat = useFeatureGate("aiChat");
  return reactive(
    useQuery({
      queryKey: ["ai_agent", "sessions"] as const,
      queryFn: listSessions,
      staleTime: 30 * 1000, // 30 seconds
      enabled: aiChat.isUnlocked,
    }),
  );
}

// Organization AI defaults, for the panel that has no session yet.
function getAgentConfig(): Promise<AIAgentConfig> {
  return api
    .get<AIAgentConfig>("/ai-agents/config/")
    .then((response) => response.data);
}

export function useAIAgentConfigQuery() {
  // Gated like the session list, but not for the same reason: this endpoint
  // is `IsAuthenticated` only (the sessions viewset is the one carrying
  // `IsAiChatEnabled`), so a locked org gets a 200 here rather than a 403.
  // The only caller mounts behind the paywall already — this keeps the
  // request from firing for a locked org if a second caller appears where
  // the sidepanel does not gate it.
  const aiChat = useFeatureGate("aiChat");
  // Keyed by organization, not merely invalidated on a switch. Invalidation
  // keeps the old data on screen while the refetch runs, so switching from a
  // gpt-5 org to a luna one left the panel offering `Minimal` for that
  // window — the answer is org-specific, so it belongs to a different cache
  // entry rather than being a stale version of the same one. A key it has
  // never seen has no data, which is the state the panel already reserves
  // space for.
  const organizationId = useActiveOrganizationId();
  return reactive(
    useQuery({
      queryKey: computed(
        () =>
          [
            "ai_agent",
            "config",
            { organizationId: organizationId.value },
          ] as const,
      ),
      queryFn: getAgentConfig,
      // Org-level configuration, changed from the admin rather than from the
      // app, so this does not need to be fresh per keystroke. It does need to
      // not outlive an admin moving the default model: session creation picks
      // that up on the next request, and a vocabulary half an hour behind it
      // is the mismatch this change removes. A minute bounds the window, and
      // a refetch over cached data is invisible — the control keeps rendering
      // the levels it already has rather than falling back to reserved space.
      staleTime: 60 * 1000,
      enabled: aiChat.isUnlocked,
    }),
  );
}

// Get a single session
function getSession(sessionId: number): Promise<AIAgentSession> {
  return api
    .get(`/ai-agents/sessions/${sessionId}/`)
    .then((response) => response.data);
}

export function useAISessionQuery(sessionId: Ref<number | null>) {
  return reactive(
    useQuery({
      queryKey: ["ai_agent", "session", sessionId] as const,
      queryFn: () => {
        if (sessionId.value === null) {
          return Promise.reject(new Error("Session ID is null"));
        }
        return getSession(sessionId.value);
      },
      enabled: computed(() => sessionId.value !== null),
      staleTime: 10 * 1000, // 10 seconds
    }),
  );
}

// Create new session
function createSession(data: {
  title?: string;
  provider?: string;
}): Promise<AIAgentSession> {
  const payload: { title: string; provider?: string } = {
    title: data.title || "New Chat",
  };
  // Only include provider if explicitly specified; otherwise let backend use org default
  if (data.provider) {
    payload.provider = data.provider;
  }
  return api
    .post("/ai-agents/sessions/", payload)
    .then((response) => response.data);
}

export function useCreateAISessionMutation() {
  const queryClient = useQueryClient();
  return reactive(
    useMutation({
      mutationFn: createSession,
      onSuccess: (newSession) => {
        // Add to sessions list
        queryClient.setQueryData<AIAgentSession[]>(
          ["ai_agent", "sessions"],
          (sessions) => [newSession, ...(sessions ?? [])],
        );
        // Cache the new session
        queryClient.setQueryData<AIAgentSession>(
          ["ai_agent", "session", newSession.id],
          newSession,
        );
      },
    }),
  );
}

// Get messages for a session
function getMessages(sessionId: number): Promise<AIAgentMessage[]> {
  return api
    .get(`/ai-agents/sessions/${sessionId}/messages/`)
    .then((response) => response.data);
}

export function useAISessionMessagesQuery(sessionId: Ref<number | null>) {
  return reactive(
    useQuery({
      queryKey: ["ai_agent", "messages", sessionId] as const,
      queryFn: () => {
        if (sessionId.value === null) {
          return Promise.reject(new Error("Session ID is null"));
        }
        return getMessages(sessionId.value);
      },
      enabled: computed(() => sessionId.value !== null),
      // No polling - we use SSE streaming for real-time updates
    }),
  );
}

// Send message to session
function sendMessage({
  sessionId,
  message,
  context,
  effort,
}: {
  sessionId: number;
  message: string;
  context?: AIAgentContext;
  effort?: ReasoningEffort;
}): Promise<ChatResponse> {
  return api
    .post(`/ai-agents/sessions/${sessionId}/chat/`, {
      message,
      context,
      effort,
    })
    .then((response) => response.data);
}

export function useSendAIMessageMutation() {
  const queryClient = useQueryClient();
  return reactive(
    useMutation({
      mutationFn: sendMessage,
      onSuccess: (_, variables) => {
        // Invalidate messages query to refetch
        queryClient.invalidateQueries({
          queryKey: ["ai_agent", "messages", variables.sessionId],
        });
        // Invalidate session query to update token count
        queryClient.invalidateQueries({
          queryKey: ["ai_agent", "session", variables.sessionId],
        });
        // Invalidate sessions list to update modified time
        queryClient.invalidateQueries({
          queryKey: ["ai_agent", "sessions"],
        });
      },
    }),
  );
}

// Delete session
function deleteSession(sessionId: number): Promise<void> {
  return api.delete(`/ai-agents/sessions/${sessionId}/`);
}

export function useDeleteAISessionMutation() {
  const queryClient = useQueryClient();
  return reactive(
    useMutation({
      mutationFn: deleteSession,
      onSuccess: (_, sessionId) => {
        // Remove from sessions list
        queryClient.setQueryData<AIAgentSession[]>(
          ["ai_agent", "sessions"],
          (sessions) => sessions?.filter((s) => s.id !== sessionId),
        );
        // Remove session cache
        queryClient.removeQueries({
          queryKey: ["ai_agent", "session", sessionId],
        });
        // Remove messages cache
        queryClient.removeQueries({
          queryKey: ["ai_agent", "messages", sessionId],
        });
      },
    }),
  );
}
